import { CheckCircle2, XCircle } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/shared/utils/cn'
import { ToastContext, type ToastOptions, type ToastTone } from './toastContext'

interface ToastItem {
  id: number
  message: string
  tone: ToastTone
}

/** Long enough to read a sentence twice; short enough not to shadow the next one. */
const TOAST_DURATION_MS = 6000

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-success-600/20 bg-success-tint text-success-600',
  error: 'border-error-600/20 bg-danger-tint text-error-600',
}

const TONE_ICONS: Record<ToastTone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(0)

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    const id = nextId.current++
    setToasts((current) => [...current, { id, message, tone: options?.tone ?? 'success' }])
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, TOAST_DURATION_MS)
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Each toast is its own role="status" live element (the Radix pattern):
          mounting an element that HAS the role announces its content, and an
          empty always-present region would collide with every other status
          element in the app — the loader, alerts — in tests and screen readers
          alike. The viewport exists only while something is showing. */}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 top-2 z-50 flex flex-col items-center gap-1 px-2">
          {toasts.map((toast) => {
            const Icon = TONE_ICONS[toast.tone]
            return (
              <div
                key={toast.id}
                role="status"
                className={cn(
                  'flex max-w-full items-center gap-1 rounded border px-2 py-1.5 shadow-raised',
                  TONE_STYLES[toast.tone]
                )}
              >
                <Icon aria-hidden="true" className="size-2.5 shrink-0" />
                <p className="text-small font-semibold">{toast.message}</p>
              </div>
            )
          })}
        </div>
      )}
    </ToastContext.Provider>
  )
}
